import React, { useCallback, useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Download, ChevronLeft, ChevronRight, Image as ImageIcon, WifiOff } from "lucide-react";
import { getCachedPhotos, getCachedValue, setCachedPhotos, setCachedValue } from "@/lib/photoCache";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { mediaStill, parseMediaValue, downloadOriginalMotionPhoto } from "@/lib/livePhotoMedia";
import { toast } from "sonner";
import { lookupPhotoTime } from "@/lib/photoTimes";
import { fmtDateTime } from "@/lib/api";

/**
 * Ticket photos are stored as data: URLs, which browsers refuse to open
 * in a new tab (blank page). This lightbox shows the full image in-app
 * with a working Download button — and when a group of photos is
 * passed, left/right arrows (+ keyboard ←/→) flip through them.
 */
function downloadDataUrl(src) {
  try {
    src = mediaStill(src);
    if (!src.startsWith("data:")) {
      const a = document.createElement("a");
      a.href = src;
      a.download = "";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    const [meta, b64] = src.split(",");
    const mime = (meta.match(/data:(.*?)[;,]/) || [])[1] || "image/jpeg";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `photo-${Date.now()}.${(mime.split("/")[1] || "jpg").replace("jpeg", "jpg")}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    console.error("photo download failed", e);
  }
}

export const PhotoLightbox = ({ photos, start = 0, alt = "photo", onClose, loadPhoto, cacheKey }) => {
  const [idx, setIdx] = useState(start);
  const [currentSrc, setCurrentSrc] = useState(photos[start] || "");
  const [photoBusy, setPhotoBusy] = useState(!!loadPhoto);
  const [photoError, setPhotoError] = useState("");
  const touchDevice = () => typeof window !== "undefined" && (
    window.matchMedia("(pointer: coarse)").matches ||
    navigator.maxTouchPoints > 0 ||
    "ontouchstart" in window ||
    window.innerWidth <= 820 ||
    /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent)
  );
  const [coarsePointer, setCoarsePointer] = useState(touchDevice);
  const many = photos.length > 1;
  const originalId = parseMediaValue(photos[idx] || "").motion_photo_id || "";
  const [takenAt, setTakenAt] = useState(null);
  useEffect(() => {
    let alive = true;
    setTakenAt(null);
    const value = currentSrc || photos[idx];
    if (value && !photoBusy) lookupPhotoTime(value).then((t) => { if (alive) setTakenAt(t); });
    return () => { alive = false; };
  }, [currentSrc, photos, idx, photoBusy]);
  const touch = React.useRef(null);
  const returnFocus = React.useRef(typeof document !== "undefined" ? document.activeElement : null);

  // Past the last photo (Next) or before the first (Back) closes the viewer
  // and returns to the ticket, instead of wrapping around.
  const prev = useCallback(() => {
    if (idx <= 0) { onClose(); return; }
    setIdx(idx - 1);
  }, [idx, onClose]);
  const next = useCallback(() => {
    if (idx >= photos.length - 1) { onClose(); return; }
    setIdx(idx + 1);
  }, [idx, photos.length, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      if (e.key === "ArrowLeft" && many) prev();
      else if (e.key === "ArrowRight" && many) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next, many]);

  useEffect(() => {
    const query = window.matchMedia("(pointer: coarse)");
    const update = () => setCoarsePointer(touchDevice());
    query.addEventListener?.("change", update);
    window.addEventListener("resize", update);
    return () => {
      query.removeEventListener?.("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const html = document.documentElement;
    const body = document.body;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const previous = {
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyWidth: body.style.width,
    };
    const preventBackgroundScroll = (event) => event.preventDefault();
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = `-${scrollX}px`;
    body.style.width = "100%";
    document.addEventListener("wheel", preventBackgroundScroll, { passive: false });
    document.addEventListener("touchmove", preventBackgroundScroll, { passive: false });
    return () => {
      document.removeEventListener("wheel", preventBackgroundScroll);
      document.removeEventListener("touchmove", preventBackgroundScroll);
      html.style.overflow = previous.htmlOverflow;
      html.style.overscrollBehavior = previous.htmlOverscroll;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.width = previous.bodyWidth;
      window.scrollTo(scrollX, scrollY);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setCurrentSrc(photos[idx] || "");
    setPhotoError("");
    if (!loadPhoto) { setPhotoBusy(false); return () => { alive = false; }; }
    setPhotoBusy(true);
    (async () => {
      const fullKey = cacheKey ? `${cacheKey}:full:${idx}` : "";
      const cached = fullKey ? await getCachedValue(fullKey) : null;
      if (!alive) return;
      if (typeof cached === "string" && mediaStill(cached).startsWith("data:image/")) {
        setCurrentSrc(cached);
        setPhotoBusy(false);
        return;
      }
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setPhotoError("This photo has not been saved for offline review yet.");
        setPhotoBusy(false);
        return;
      }
      try {
        const full = await loadPhoto(idx);
        if (!alive) return;
        if (!full) throw new Error("empty photo");
        setCurrentSrc(full);
        if (fullKey) await setCachedValue(fullKey, full);
      } catch {
        if (alive) setPhotoError("Could not load this photo. Try again when your connection is stronger.");
      } finally {
        if (alive) setPhotoBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [idx, photos, loadPhoto, cacheKey]);

  // Swipe left/right on phones and tablets flips through the photos.
  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (many && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next(); else prev();
    }
  };

  const lightbox = (
    <DialogPrimitive.Content
      className="fixed inset-0 z-[1000] h-[100dvh] w-screen overflow-hidden bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} viewer`}
      aria-describedby={undefined}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        if (returnFocus.current?.isConnected) returnFocus.current.focus();
      }}
      data-testid="photo-lightbox"
    >
      <DialogPrimitive.Title className="sr-only" data-testid="photo-lightbox-title">{alt} viewer</DialogPrimitive.Title>
      {/* keep the controls below the iPhone notch / status bar */}
      <div
        className="absolute right-3 flex gap-2"
        style={{ top: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => downloadDataUrl(currentSrc)}
          disabled={!currentSrc || photoBusy}
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-3 py-2 rounded-sm border border-white/20"
          data-testid="photo-lightbox-download"
        >
          <Download className="w-4 h-4" /> <span className="hidden sm:inline">Download</span>
        </button>
        {originalId && (
          <button
            type="button"
            onClick={async () => {
              try { await downloadOriginalMotionPhoto(originalId, "motion"); }
              catch { toast.error("Could not download the original motion file."); }
            }}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-3 py-2 rounded-sm border border-white/20"
            data-testid="photo-lightbox-original"
            title="Download the original Live Photo motion (untouched)"
          >
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">Original</span>
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center bg-white/15 hover:bg-white/30 text-white p-2.5 rounded-full border border-white/25"
          aria-label="Close"
          data-testid="photo-lightbox-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {takenAt && (
        <div className="absolute left-3 text-white/90 text-xs sm:text-sm font-semibold bg-white/10 border border-white/20 px-3 py-1.5 rounded-full"
          style={{ top: "calc(0.75rem + env(safe-area-inset-top, 0px))" }} onClick={(e) => e.stopPropagation()} data-testid="photo-lightbox-taken-at">
          Taken {fmtDateTime(takenAt)}
        </div>
      )}
      {many && (
        <>
          {!coarsePointer && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full border border-white/20"
                aria-label="Previous photo"
                data-testid="photo-lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full border border-white/20"
                aria-label="Next photo"
                data-testid="photo-lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/90 text-sm font-semibold bg-white/10 border border-white/20 px-3 py-1 rounded-full"
            data-testid="photo-lightbox-counter"
          >
            {idx + 1} / {photos.length}
          </div>
        </>
      )}

      <div className="relative max-w-full max-h-[85dvh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        <LivePhotoSurface
          value={currentSrc || photos[idx]}
          alt={`${alt} ${idx + 1}`}
          className="max-w-full max-h-[85dvh] rounded-sm shadow-2xl"
          imageClassName="max-w-full max-h-[85dvh] object-contain"
          testId="photo-lightbox-img"
          eagerMotion
          autoPlayOnce
        />
        {photoBusy && (
          <div className="absolute inset-0 bg-black/35 text-white flex items-center justify-center text-sm font-semibold" data-testid="photo-lightbox-loading">Loading photo…</div>
        )}
        {photoError && (
          <div className="absolute inset-x-3 bottom-3 bg-black/80 border border-white/20 text-white text-sm text-center p-3 rounded-sm" data-testid="photo-lightbox-error">{photoError}</div>
        )}
      </div>
    </DialogPrimitive.Content>
  );
  return <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogPrimitive.Portal>{lightbox}</DialogPrimitive.Portal>
  </DialogPrimitive.Root>;
};

/**
 * Photo strip that works with the lightweight list payloads: when the
 * ticket/note arrives with real `photos` (e.g. right after upload) they
 * render immediately; when it only carries a `photo_count`, a compact
 * "View N photos" chip fetches them on demand — keeping list loads fast.
 * Once fetched, photos are saved to an on-device IndexedDB cache keyed
 * by `cacheKey` so returning to this ticket later (even after a reload)
 * shows them instantly with no network wait.
 */
export const LazyPhotoStrip = ({ photos, count = 0, fetchPhotos, fetchManifest, fetchPhoto, size = "w-20 h-20", alt = "photo", testid, cacheKey, buttonClassName = "" }) => {
  const [loaded, setLoaded] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(!cacheKey);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (!cacheKey || (photos && photos.length)) { setCacheChecked(true); return; }
    let alive = true;
    const cacheLookup = fetchManifest ? getCachedValue(`${cacheKey}:manifest`) : getCachedPhotos(cacheKey);
    cacheLookup.then((cached) => {
      if (!alive) return;
      if (cached && cached.length) {
        if (fetchManifest) setManifest(cached); else setLoaded(cached);
      }
      setCacheChecked(true);
    });
    return () => { alive = false; };
  }, [cacheKey, photos, fetchManifest]);

  const list = (photos && photos.length ? photos : loaded) || [];
  if (list.length) {
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid ? `${testid}-strip` : undefined}>
        {list.map((p, i) => (
          <PhotoThumb key={i} src={p} photos={list} index={i} alt={`${alt} ${i + 1}`} size={size} testid={testid ? `${testid}-${i}` : undefined} />
        ))}
      </div>
    );
  }
  const manifestList = manifest || [];
  if (manifestList.length) {
    const thumbnails = manifestList.map((item) => item.thumbnail);
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid ? `${testid}-manifest` : undefined}>
        {manifestList.map((item, i) => (
          <PhotoThumb
            key={item.index}
            src={item.thumbnail}
            photos={thumbnails}
            index={i}
            alt={`${alt} ${i + 1}`}
            size={size}
            testid={testid ? `${testid}-${i}` : undefined}
            loadPhoto={fetchPhoto ? (displayIndex) => fetchPhoto(manifestList[displayIndex].index) : undefined}
            cacheKey={cacheKey}
          />
        ))}
      </div>
    );
  }
  if (!count || !cacheChecked) return null;
  // Offline with nothing cached on this device — a network fetch would just
  // fail, so tell the worker plainly instead of a dead "View photos" button.
  if (!online) {
    return (
      <div
        className={`inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground border border-dashed border-border rounded-sm px-2.5 py-1.5 ${buttonClassName}`}
        data-testid={testid ? `${testid}-offline` : undefined}
      >
        <WifiOff className="w-3.5 h-3.5" />
        {`${count} photo${count > 1 ? "s" : ""} — not saved offline`}
      </div>
    );
  }
  const load = async () => {
    setBusy(true);
    try {
      if (fetchManifest) {
        const result = (await fetchManifest()) || [];
        setManifest(result);
        if (cacheKey && result.length) await setCachedValue(`${cacheKey}:manifest`, result);
      } else {
        const result = (await fetchPhotos()) || [];
        setLoaded(result);
        if (cacheKey && result.length) await setCachedPhotos(cacheKey, result);
      }
    } catch (e) {
      console.error("photo load failed", e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={load}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-sm px-2.5 py-1.5 hover:bg-secondary/50 text-muted-foreground ${buttonClassName}`}
      data-testid={testid ? `${testid}-load` : undefined}
    >
      <ImageIcon className="w-3.5 h-3.5" />
      {busy ? "Loading…" : `View ${count} photo${count > 1 ? "s" : ""}`}
    </button>
  );
};

export const PhotoThumb = ({ src, photos, index = 0, alt = "photo", size = "w-20 h-20", testid, loadPhoto, cacheKey }) => {
  const [open, setOpen] = useState(false);
  const group = photos && photos.length ? photos : [src];
  return (
    <>
      <LivePhotoSurface value={src} alt={alt} className={`${size} rounded-sm border border-border cursor-zoom-in`} testId={testid} onActivate={() => setOpen(true)} />
      {open && <PhotoLightbox photos={group} start={index} alt={alt} onClose={() => setOpen(false)} loadPhoto={loadPhoto} cacheKey={cacheKey} />}
    </>
  );
};
